import { ArrowLeft, ArrowUpDown, Info, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/base/table';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PaginacaoCompacta } from '@/ui/PaginacaoCompacta';
import { Spinner } from '@/ui/Spinner';

import { ComoFuncionaAIntegracao } from '../catalogo-veiculos/ComoFuncionaAIntegracao';
import { DialogoDeCadastro } from './DialogoDeCadastro';
import type { FiltrosDoCadastro, LinhaDoCadastro } from './cadastros.contrato';
import {
  TAMANHO_DA_PAGINA,
  mensagemDoErro,
  useListaDoCadastro,
  useMudarSituacao,
  useOpcoesDeMarca,
} from './cadastros.dados';
import { formatarValor, type DefinicaoDoCadastro } from './cadastros.definicoes';

/** Espera a pessoa parar de digitar antes de perguntar ao servidor. */
const ESPERA_DA_BUSCA_MS = 300;

/**
 * Tela de um cadastro de referência, lida da API: cabeçalho, filtros, tabela e
 * os quatro estados exigidos pelo CLAUDE.md. A paginação, a busca, o filtro de
 * situação e a ordem são do servidor — a tela nunca fatia uma coleção já
 * carregada.
 *
 * AS COLUNAS SEGUEM O PROTÓTIPO, no nome e na ordem:
 * `Nome · [campos próprios] · Ordem · Saúde · Status · Ações`.
 *
 * Quem pode escrever vê `Novo` e a coluna `Ações` (editar, inativar e reativar),
 * sempre por último (regra 10 de interface). Escolas são só de consulta e não
 * trazem nenhuma das duas — regra 7: o que não se pode fazer não é renderizado.
 * Não há coluna de seleção, porque não há ação em lote.
 *
 * A coluna `Saúde` continua VAZIA: ela é juízo calculado por regra de validação
 * que ainda não foi construída, e preenchê-la mostraria um diagnóstico que
 * ninguém apurou.
 */
export function ListaDeCadastro({ definicao }: { definicao: DefinicaoDoCadastro }) {
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<FiltrosDoCadastro['situacao']>('todos');
  const [marcaId, setMarcaId] = useState<string>('todas');
  const [pagina, setPagina] = useState(1);
  const [ordem, setOrdem] = useState<{ campo: string; crescente: boolean } | null>(null);
  const [janela, setJanela] = useState<{ registro?: LinhaDoCadastro } | null>(null);

  useEffect(() => {
    const espera = setTimeout(() => {
      setBusca(digitado);
      setPagina(1);
    }, ESPERA_DA_BUSCA_MS);
    return () => clearTimeout(espera);
  }, [digitado]);

  const filtraPorMarca =
    definicao.chave === 'modelos-veiculo' || definicao.chave === 'versoes-veiculo';
  const marcas = useOpcoesDeMarca(filtraPorMarca);

  const filtros = useMemo<FiltrosDoCadastro>(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca,
      situacao,
      ordenarPor: ordem?.campo ?? null,
      sentido: ordem?.crescente === false ? 'desc' : 'asc',
      marcaId: filtraPorMarca && marcaId !== 'todas' ? marcaId : undefined,
    }),
    [pagina, busca, situacao, ordem, marcaId, filtraPorMarca],
  );

  const consulta = useListaDoCadastro(definicao.grupo, definicao.chave, filtros);
  const mudarSituacao = useMudarSituacao(definicao.grupo, definicao.chave);

  const gravavel = !definicao.somenteConsulta;
  const linhas = consulta.data?.itens ?? [];
  const total = consulta.data?.total ?? 0;
  const filtrando = busca.trim() !== '' || situacao !== 'todos' || marcaId !== 'todas';

  function alternarOrdem(campo: string) {
    setPagina(1);
    setOrdem((atual) =>
      atual?.campo === campo ? { campo, crescente: !atual.crescente } : { campo, crescente: true },
    );
  }

  function limparFiltros() {
    setDigitado('');
    setBusca('');
    setSituacao('todos');
    setMarcaId('todas');
    setPagina(1);
  }

  function trocarSituacao(linha: LinhaDoCadastro) {
    if (!linha.versao) return;
    const ativar = linha.situacao === 'inativo';
    mudarSituacao.mutate(
      { id: linha.id, ativar, versao: linha.versao },
      {
        onSuccess: () =>
          toast.success(
            `${definicao.nomeDaEntidade} ${
              definicao.genero === 'a'
                ? ativar
                  ? 'reativada'
                  : 'inativada'
                : ativar
                  ? 'reativado'
                  : 'inativado'
            }.`,
          ),
        onError: (erro) =>
          toast.error(mensagemDoErro(erro, 'Não foi possível alterar a situação. Tente de novo.')),
      },
    );
  }

  const rotuloDaSituacao = (s: LinhaDoCadastro['situacao']) =>
    s === 'inativo'
      ? definicao.situacaoFeminina
        ? 'Inativa'
        : 'Inativo'
      : definicao.situacaoFeminina
        ? 'Ativa'
        : 'Ativo';

  const botaoNovo = gravavel ? (
    definicao.comDetalhe ? (
      <Button asChild>
        <Link to={`${definicao.caminho}/nova`}>
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          {definicao.genero === 'a' ? 'Nova' : 'Novo'} {definicao.nomeDaEntidade.toLowerCase()}
        </Link>
      </Button>
    ) : (
      <Button type="button" onClick={() => setJanela({})}>
        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
        {definicao.genero === 'a' ? 'Nova' : 'Novo'} {definicao.nomeDaEntidade.toLowerCase()}
      </Button>
    )
  ) : null;

  const detalhe = (linha: LinhaDoCadastro): string | undefined =>
    definicao.comDetalhe && linha.slug ? `${definicao.caminho}/${linha.slug}` : undefined;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={definicao.hub}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {definicao.rotuloDoHub}
        </Link>
      </div>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">{definicao.titulo}</h1>
          <p className="text-sm text-muted-foreground">{definicao.descricao}</p>
        </div>
        {(definicao.integracaoComFonte || botaoNovo) && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {definicao.integracaoComFonte && <ComoFuncionaAIntegracao />}
            {botaoNovo}
          </div>
        )}
      </header>

      {definicao.aviso && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{definicao.aviso}</AlertDescription>
        </Alert>
      )}

      {consulta.isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center gap-3 py-16">
            <Spinner />
            <p className="text-sm text-muted-foreground">
              Carregando {definicao.titulo.toLowerCase()}…
            </p>
          </CardContent>
        </Card>
      )}

      {consulta.isError && !consulta.data && (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar"
          description={mensagemDoErro(
            consulta.error,
            'Tente de novo. Se continuar, avise a administração do sistema.',
          )}
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
          secondaryAction={{ label: definicao.rotuloDoHub, to: definicao.hub, variant: 'outline' }}
        />
      )}

      {consulta.data && (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={digitado}
                onChange={(e) => setDigitado(e.target.value)}
                placeholder="Buscar nesta lista…"
                aria-label={`Buscar em ${definicao.titulo.toLowerCase()}`}
                className="pl-9"
              />
            </div>

            {filtraPorMarca && (
              <Select
                value={marcaId}
                onValueChange={(valor) => {
                  setMarcaId(valor);
                  setPagina(1);
                }}
              >
                <SelectTrigger className="sm:w-52" aria-label="Filtrar por marca">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as marcas</SelectItem>
                  {(marcas.data?.itens ?? []).map((marca) => (
                    <SelectItem key={marca.id} value={marca.id}>
                      {String(marca.nome)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select
              value={situacao}
              onValueChange={(valor) => {
                setSituacao(valor as FiltrosDoCadastro['situacao']);
                setPagina(1);
              }}
            >
              <SelectTrigger className="sm:w-52" aria-label="Filtrar por situação">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as situações</SelectItem>
                <SelectItem value="ativo">Apenas ativos</SelectItem>
                <SelectItem value="inativo">Apenas inativos</SelectItem>
              </SelectContent>
            </Select>

            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              {total} {total === 1 ? 'registro' : 'registros'}
            </p>
          </div>

          {consulta.isError && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                Não foi possível atualizar a lista. Os dados abaixo podem estar desatualizados.
              </AlertDescription>
            </Alert>
          )}

          {total === 0 && !filtrando ? (
            <EmptyState
              title="Nenhum registro cadastrado"
              description={`${definicao.titulo} ainda não tem nenhum registro. Assim que houver, ele aparece aqui.`}
              actionLabel={gravavel && !definicao.comDetalhe ? 'Novo registro' : undefined}
              onAction={gravavel && !definicao.comDetalhe ? () => setJanela({}) : undefined}
            />
          ) : total === 0 ? (
            <EmptyState
              variant="search"
              title="Nenhum registro corresponde"
              description="Nenhum registro atende à busca e ao filtro escolhidos."
              actionLabel="Limpar busca e filtro"
              onAction={limparFiltros}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableCaption className="sr-only">{definicao.descricao}</TableCaption>
                    <TableHeader>
                      <TableRow>
                        {definicao.colunas.map((coluna) => {
                          const campo =
                            typeof coluna.campo === 'string' ? coluna.campo : coluna.ordenarPor;
                          return (
                            <TableHead
                              key={coluna.titulo}
                              className={coluna.numerica ? 'text-right' : undefined}
                              scope="col"
                              aria-sort={
                                ordem && campo && ordem.campo === campo
                                  ? ordem.crescente
                                    ? 'ascending'
                                    : 'descending'
                                  : undefined
                              }
                            >
                              {campo ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="-ml-2 h-7 gap-1 px-2 font-medium"
                                  onClick={() => alternarOrdem(campo)}
                                >
                                  {coluna.titulo}
                                  <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
                                </Button>
                              ) : (
                                coluna.titulo
                              )}
                            </TableHead>
                          );
                        })}
                        {definicao.colunasDeControle && (
                          <>
                            <TableHead
                              scope="col"
                              className="text-right"
                              aria-sort={
                                ordem?.campo === 'ordem'
                                  ? ordem.crescente
                                    ? 'ascending'
                                    : 'descending'
                                  : undefined
                              }
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-2 h-7 gap-1 px-2 font-medium"
                                onClick={() => alternarOrdem('ordem')}
                              >
                                Ordem
                                <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
                              </Button>
                            </TableHead>
                            <TableHead scope="col">Saúde</TableHead>
                          </>
                        )}
                        <TableHead
                          scope="col"
                          aria-sort={
                            ordem?.campo === 'situacao'
                              ? ordem.crescente
                                ? 'ascending'
                                : 'descending'
                              : undefined
                          }
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            className="-ml-2 h-7 gap-1 px-2 font-medium"
                            onClick={() => alternarOrdem('situacao')}
                          >
                            Status
                            <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
                          </Button>
                        </TableHead>
                        {gravavel && (
                          <TableHead scope="col" className="text-right">
                            Ações
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linhas.map((linha) => {
                        const destino = detalhe(linha);
                        const nome = String(linha.nome ?? linha.codigo ?? '');
                        const trocando =
                          mudarSituacao.isPending && mudarSituacao.variables?.id === linha.id;
                        return (
                          <TableRow
                            key={linha.id}
                            className={destino ? 'hover:bg-muted/50' : undefined}
                          >
                            {definicao.colunas.map((coluna, posicao) => {
                              const conteudo =
                                typeof coluna.campo === 'function'
                                  ? coluna.campo(linha)
                                  : formatarValor(linha[coluna.campo]);
                              return (
                                <TableCell
                                  key={coluna.titulo}
                                  className={
                                    coluna.numerica ? 'text-right tabular-nums' : undefined
                                  }
                                >
                                  {/*
                                    O link cobre a primeira célula e recebe o nome
                                    acessível da linha: o teclado alcança o detalhe
                                    uma vez por linha, e o leitor de tela anuncia o
                                    destino.
                                  */}
                                  {destino && posicao === 0 ? (
                                    <Link
                                      to={destino}
                                      className="block font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                      {conteudo}
                                    </Link>
                                  ) : (
                                    conteudo
                                  )}
                                </TableCell>
                              );
                            })}
                            {definicao.colunasDeControle && (
                              <>
                                <TableCell className="text-right tabular-nums">
                                  {formatarValor(linha.ordem)}
                                </TableCell>
                                {/* Vazia de propósito: ver o comentário no topo do arquivo. */}
                                <TableCell className="text-muted-foreground">—</TableCell>
                              </>
                            )}
                            <TableCell>
                              <Badge
                                variant={linha.situacao === 'inativo' ? 'secondary' : 'default'}
                              >
                                {rotuloDaSituacao(linha.situacao)}
                              </Badge>
                            </TableCell>
                            {gravavel && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  {definicao.comDetalhe ? (
                                    <Button asChild variant="ghost" size="sm">
                                      <Link
                                        to={`${definicao.caminho}/${linha.slug}/editar`}
                                        aria-label={`Editar ${nome}`}
                                      >
                                        Editar
                                      </Link>
                                    </Button>
                                  ) : (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`Editar ${nome}`}
                                      onClick={() => setJanela({ registro: linha })}
                                    >
                                      Editar
                                    </Button>
                                  )}
                                  {linha.situacao === 'inativo' ? (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      disabled={trocando}
                                      aria-label={`Reativar ${nome}`}
                                      onClick={() => trocarSituacao(linha)}
                                    >
                                      Reativar
                                    </Button>
                                  ) : (
                                    <ConfirmDialog
                                      trigger={
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          disabled={trocando}
                                          aria-label={`Inativar ${nome}`}
                                        >
                                          Inativar
                                        </Button>
                                      }
                                      title={`Inativar ${nome}?`}
                                      description="O item deixa de ser oferecido nas listas de escolha. Os registros que já o usam continuam como estão, e ele pode ser reativado quando quiser."
                                      confirmLabel="Inativar"
                                      onConfirm={() => trocarSituacao(linha)}
                                    />
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <PaginacaoCompacta
            pagina={pagina}
            tamanho={TAMANHO_DA_PAGINA}
            total={total}
            aoMudar={setPagina}
          />
        </div>
      )}

      {janela && (
        <DialogoDeCadastro
          key={janela.registro?.id ?? 'novo'}
          definicao={definicao}
          registro={janela.registro}
          aoFechar={() => setJanela(null)}
        />
      )}
    </div>
  );
}
